<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import camelcaseKeys from 'camelcase-keys';
import ContactAPI from 'dashboard/api/contacts';

defineProps({
  selectedContact: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['update:selectedContact']);

const { t } = useI18n();

const CONTACT_PAGE_SIZE = 15;
const scrollLoadThreshold = 96;

const contactQuery = ref('');
const contactResults = ref([]);
const contactMeta = ref({});
const contactSearchError = ref('');
const contactPaginationError = ref('');
const isSearchingContacts = ref(false);
const isLoadingMoreContacts = ref(false);
let contactRequestSequence = 0;

const hasMoreContacts = computed(() => {
  if (contactMeta.value?.has_more !== undefined) {
    return contactMeta.value.has_more === true;
  }

  return (
    contactResults.value.length > 0 &&
    contactResults.value.length % CONTACT_PAGE_SIZE === 0
  );
});
const reachedEndOfContacts = computed(
  () => contactResults.value.length > 0 && !hasMoreContacts.value
);

/**
 * Converts contact payloads into the selector shape.
 *
 * @param {Object} contact Contact API payload.
 * @returns {Object} Contact selector row.
 */
function normalizeContact(contact) {
  return camelcaseKeys(contact || {}, { deep: true });
}

/**
 * Returns the visible name for a contact result.
 *
 * @param {Object} contact Contact selector row.
 * @returns {string} Contact display name.
 */
function contactDisplayName(contact) {
  return (
    contact?.name ||
    contact?.email ||
    contact?.phoneNumber ||
    contact?.phone_number ||
    t('CRM_EVENTS_FEED.UNKNOWN_CONTACT')
  );
}

/**
 * Builds secondary contact context for the selector row.
 *
 * @param {Object} contact Contact selector row.
 * @returns {string} Email and phone context.
 */
function contactSubtitle(contact) {
  return [contact.email, contact.phoneNumber || contact.phone_number]
    .filter(Boolean)
    .join(' / ');
}

/**
 * Fetches one contact page, preserving the current search keyword.
 *
 * @param {number} page Page number to request.
 * @returns {Promise<Object>} Axios response.
 */
function fetchContactPage(page) {
  const query = contactQuery.value.trim();
  if (query) {
    return ContactAPI.search(query, page, 'name');
  }

  return ContactAPI.get(page, 'name');
}

/**
 * Loads the first contact page for the current keyword, or the default contact
 * list when no keyword is present.
 *
 * @returns {Promise<void>}
 */
async function runContactSearch() {
  contactRequestSequence += 1;
  const sequence = contactRequestSequence;
  isSearchingContacts.value = true;
  contactSearchError.value = '';
  contactPaginationError.value = '';

  try {
    const response = await fetchContactPage(1);
    if (sequence !== contactRequestSequence) return;

    contactResults.value = Array.isArray(response.data?.payload)
      ? response.data.payload.map(normalizeContact)
      : [];
    contactMeta.value = response.data?.meta || {};
  } catch {
    if (sequence !== contactRequestSequence) return;

    contactResults.value = [];
    contactMeta.value = {};
    contactSearchError.value = t('CRM_EVENT_CREATE.CONTACT_SEARCH_ERROR');
  } finally {
    if (sequence === contactRequestSequence) {
      isSearchingContacts.value = false;
    }
  }
}

/**
 * Appends the next contact page for the current keyword.
 *
 * @returns {Promise<void>}
 */
async function loadMoreContacts() {
  if (
    isSearchingContacts.value ||
    isLoadingMoreContacts.value ||
    !hasMoreContacts.value
  ) {
    return;
  }

  const sequence = contactRequestSequence;
  const nextPage = Number(contactMeta.value.current_page || 1) + 1;
  isLoadingMoreContacts.value = true;
  contactPaginationError.value = '';

  try {
    const response = await fetchContactPage(nextPage);
    if (sequence !== contactRequestSequence) return;

    const existingIds = new Set(
      contactResults.value.map(contact => contact.id)
    );
    const nextContacts = Array.isArray(response.data?.payload)
      ? response.data.payload
          .map(normalizeContact)
          .filter(contact => !existingIds.has(contact.id))
      : [];

    contactResults.value = [...contactResults.value, ...nextContacts];
    contactMeta.value = response.data?.meta || {};
  } catch {
    if (sequence !== contactRequestSequence) return;

    contactPaginationError.value = t(
      'CRM_EVENT_CREATE.CONTACT_LOAD_MORE_ERROR'
    );
  } finally {
    if (sequence === contactRequestSequence) {
      isLoadingMoreContacts.value = false;
    }
  }
}

/**
 * Emits the selected contact to the parent dialog.
 *
 * @param {Object} contact Contact selector row.
 * @returns {void}
 */
function selectContact(contact) {
  emit('update:selectedContact', contact);
}

/**
 * Loads more contacts when the selector list reaches the bottom.
 *
 * @param {Event} event Scroll event from the contact list container.
 * @returns {void}
 */
function handleContactListScroll(event) {
  const target = event.currentTarget;
  if (!target) return;

  const distanceToBottom =
    target.scrollHeight - target.scrollTop - target.clientHeight;
  if (distanceToBottom <= scrollLoadThreshold) {
    loadMoreContacts();
  }
}

watch(contactQuery, runContactSearch);
onMounted(runContactSearch);
</script>

<template>
  <label class="flex flex-col gap-1.5 text-sm">
    <span class="font-medium text-n-slate-12">
      {{ t('CRM_EVENT_CREATE.CONTACT') }}
    </span>
    <input
      v-model="contactQuery"
      data-testid="crm-contact-search-input"
      type="search"
      class="h-10 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
      :placeholder="t('CRM_EVENT_CREATE.SEARCH_CONTACT')"
    />
    <p v-if="contactSearchError" class="text-xs text-n-ruby-10">
      {{ contactSearchError }}
    </p>
    <p v-else-if="isSearchingContacts" class="text-xs text-n-slate-10">
      {{ t('CRM_EVENT_CREATE.SEARCHING') }}
    </p>
    <div
      v-else-if="contactResults.length"
      data-testid="crm-contact-results"
      class="max-h-[5.25rem] overflow-y-auto rounded-md border border-n-weak bg-n-solid-1"
      @scroll="handleContactListScroll"
    >
      <button
        v-for="contact in contactResults"
        :key="contact.id"
        data-testid="crm-contact-result"
        type="button"
        class="flex min-h-14 w-full flex-col items-start justify-center px-3 py-2 text-start hover:bg-n-alpha-2"
        :class="{ 'bg-n-alpha-2': selectedContact?.id === contact.id }"
        @click="selectContact(contact)"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{ contactDisplayName(contact) }}
        </span>
        <span v-if="contactSubtitle(contact)" class="text-xs text-n-slate-10">
          {{ contactSubtitle(contact) }}
        </span>
      </button>
      <p
        v-if="isLoadingMoreContacts"
        data-testid="crm-contacts-loading-more"
        class="px-3 py-3 text-center text-xs text-n-slate-10"
      >
        {{ t('CRM_EVENT_CREATE.CONTACT_LOADING_MORE') }}
      </p>
      <p
        v-else-if="contactPaginationError"
        data-testid="crm-contacts-pagination-error"
        class="px-3 py-3 text-center text-xs text-n-ruby-10"
      >
        {{ contactPaginationError }}
      </p>
      <p
        v-else-if="reachedEndOfContacts"
        data-testid="crm-contacts-end-of-list"
        class="px-3 py-3 text-center text-xs text-n-slate-10"
      >
        {{ t('CRM_EVENT_CREATE.CONTACT_END_OF_LIST') }}
      </p>
    </div>
  </label>
</template>
